import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Lightbulb, Bug, Heart, Trash2, Check, RefreshCw, Users, Inbox } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAT_META = {
  suggestion: { label: 'Sugestão', Icon: Lightbulb, badgeCls: 'bg-amber-100 text-amber-800 border-amber-200' },
  bug:        { label: 'Bug',      Icon: Bug,       badgeCls: 'bg-rose-100 text-rose-800 border-rose-200' },
  praise:     { label: 'Elogio',   Icon: Heart,     badgeCls: 'bg-pink-100 text-pink-800 border-pink-200' },
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const TeacherFeedback = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ classrooms: [], total: 0, unread: 0 });
  const [unreadOnly, setUnreadOnly] = useState(false);
  // 'all' | 'suggestion' | 'bug' | 'praise' — client-side filter on top of
  // the server payload; keeps the server contract simple.
  const [categoryFilter, setCategoryFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const url = new URL(`${BACKEND_URL}/api/teacher/feedback`);
      if (unreadOnly) url.searchParams.set('unread_only', 'true');
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Falha ao carregar feedbacks');
      const body = await res.json();
      setData(body);
    } catch (e) {
      toast.error(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unreadOnly]);

  // Live push: reload the list when a new feedback arrives so the page stays
  // in sync without the teacher having to hit "Atualizar".
  useEffect(() => {
    let ws = null;
    let closed = false;
    (async () => {
      try {
        const me = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!me.ok) return;
        const info = await me.json();
        const teacherId = info?.user_id;
        if (!teacherId || closed) return;
        const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + `/api/ws/teacher/${teacherId}`;
        ws = new WebSocket(wsUrl);
        ws.onmessage = (ev) => {
          try {
            const payload = JSON.parse(ev.data);
            if (payload?.type === 'feedback:new') {
              load();
              toast.info('Novo feedback recebido');
            }
          } catch (_) { /* ping */ }
        };
      } catch (_) { /* silent */ }
    })();
    return () => { closed = true; if (ws && ws.readyState <= 1) ws.close(); };
    // eslint-disable-next-line
  }, []);

  const markRead = async (fbId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/teacher/feedback/${fbId}/mark_read`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error('Falha');
      load();
    } catch (e) { toast.error('Erro ao marcar como lido'); }
  };

  const remove = async (fbId) => {
    if (!window.confirm('Excluir este feedback?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/teacher/feedback/${fbId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Falha');
      toast.success('Feedback excluído');
      load();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="teacher-feedback-page">
      {/* Top nav */}
      <nav className="theme-navbar border-b bg-white sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/teacher/dashboard')} data-testid="feedback-back-btn">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </Button>
            <h1 className="text-xl font-outfit font-black text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Feedback dos Alunos
              {data.unread > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-black bg-rose-500 text-white" data-testid="unread-badge">
                  {data.unread} novo{data.unread === 1 ? '' : 's'}
                </span>
              )}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={unreadOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setUnreadOnly((v) => !v)}
              data-testid="toggle-unread-btn"
            >
              {unreadOnly ? 'Mostrando não lidos' : 'Só não lidos'}
            </Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="feedback-refresh-btn">
              <RefreshCw className="w-4 h-4" /> Atualizar
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Category filter pills — client-side subset of the full payload */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="category-filter-strip">
          {[
            { key: 'all',        label: 'Todos',    Icon: MessageSquare, active: 'bg-slate-900 text-white' },
            { key: 'suggestion', label: 'Sugestões',Icon: Lightbulb,     active: 'bg-amber-500 text-white' },
            { key: 'bug',        label: 'Bugs',     Icon: Bug,           active: 'bg-rose-500 text-white' },
            { key: 'praise',     label: 'Elogios',  Icon: Heart,         active: 'bg-pink-500 text-white' },
          ].map(({ key, label, Icon, active }) => {
            const isActive = categoryFilter === key;
            const totalOfCat = key === 'all'
              ? data.total
              : (data.classrooms || []).reduce(
                  (acc, c) => acc + c.students.reduce(
                    (a2, s) => a2 + s.feedbacks.filter((f) => f.category === key).length, 0), 0);
            return (
              <button
                key={key}
                type="button"
                onClick={() => setCategoryFilter(key)}
                data-testid={`filter-cat-${key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-outfit font-bold text-sm transition-all ${
                  isActive
                    ? `${active} border-transparent shadow-md -translate-y-0.5`
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={2.4} />
                {label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full tabular-nums ${
                  isActive ? 'bg-white/25' : 'bg-slate-100 text-slate-500'
                }`}>{totalOfCat}</span>
              </button>
            );
          })}
        </div>

        {(() => {
          // Derive a filtered view — drop feedbacks of other categories, and
          // drop students / classrooms that become empty. This keeps the
          // grouped rendering below oblivious to the filter.
          const filtered = categoryFilter === 'all'
            ? data
            : (() => {
                const classrooms = (data.classrooms || [])
                  .map((c) => {
                    const students = c.students
                      .map((s) => ({
                        ...s,
                        feedbacks: s.feedbacks.filter((f) => f.category === categoryFilter),
                      }))
                      .filter((s) => s.feedbacks.length > 0);
                    const total = students.reduce((a, s) => a + s.feedbacks.length, 0);
                    const unread = students.reduce(
                      (a, s) => a + s.feedbacks.filter((f) => !f.read).length, 0);
                    return { ...c, students, total, unread };
                  })
                  .filter((c) => c.students.length > 0);
                return {
                  ...data,
                  classrooms,
                  total: classrooms.reduce((a, c) => a + c.total, 0),
                  unread: classrooms.reduce((a, c) => a + c.unread, 0),
                };
              })();

          if (loading) {
            return (
              <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" /> Carregando feedbacks...
              </div>
            );
          }
          if (filtered.classrooms.length === 0) {
            return (
              <div className="rounded-2xl border-2 border-dashed border-slate-200 py-16 text-center" data-testid="feedback-empty">
                <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="font-outfit font-bold text-lg text-slate-700">
                  {categoryFilter === 'all' ? 'Nenhum feedback ainda' : 'Nenhum feedback nesta categoria'}
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {unreadOnly
                    ? 'Todos os feedbacks já foram lidos.'
                    : (categoryFilter === 'all'
                        ? 'Seus alunos ainda não enviaram sugestões, bugs ou elogios.'
                        : 'Tente outra categoria ou volte a "Todos".')}
                </p>
              </div>
            );
          }
          return (
          <div className="space-y-8">
            {filtered.classrooms.map((c) => (
              <section key={c.classroom_id} className="rounded-2xl border-2 border-slate-100 bg-white overflow-hidden" data-testid={`classroom-block-${c.classroom_id}`}>
                <header className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-500" />
                    <h2 className="font-outfit font-black text-slate-900">{c.classroom_name || 'Turma'}</h2>
                    <Badge variant="secondary" className="ml-1 text-[10px]">{c.total} mensagem{c.total === 1 ? '' : 's'}</Badge>
                    {c.unread > 0 && (
                      <Badge className="bg-rose-500 hover:bg-rose-500 text-white text-[10px]">{c.unread} nova{c.unread === 1 ? '' : 's'}</Badge>
                    )}
                  </div>
                </header>

                <div className="divide-y divide-slate-100">
                  {c.students.map((s) => (
                    <div key={s.student_id} className="px-5 py-4" data-testid={`student-block-${s.student_id}`}>
                      <p className="font-outfit font-bold text-slate-800 mb-3">
                        {s.user_name}
                        {s.monster_name && <span className="text-xs text-slate-400 font-normal ml-2">— {s.monster_name}</span>}
                      </p>
                      <ul className="space-y-2">
                        {s.feedbacks.map((fb) => {
                          const meta = CAT_META[fb.category] || CAT_META.suggestion;
                          const CatIcon = meta.Icon;
                          return (
                            <li
                              key={fb.feedback_id}
                              className={`relative rounded-xl border-2 p-3 pr-24 ${fb.read ? 'bg-slate-50 border-slate-100' : 'bg-amber-50/40 border-amber-200'}`}
                              data-testid={`feedback-item-${fb.feedback_id}`}
                            >
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border ${meta.badgeCls}`}>
                                  <CatIcon className="w-3 h-3" strokeWidth={2.5} />
                                  {meta.label}
                                </span>
                                <span className="text-[11px] text-slate-500">{formatDate(fb.created_at)}</span>
                                {!fb.read && <span className="inline-block w-2 h-2 rounded-full bg-rose-500" title="Não lido" />}
                              </div>
                              <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid={`feedback-text-${fb.feedback_id}`}>
                                {fb.message}
                              </p>
                              <div className="absolute top-2 right-2 flex items-center gap-1">
                                {!fb.read && (
                                  <button
                                    onClick={() => markRead(fb.feedback_id)}
                                    className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-700"
                                    title="Marcar como lido"
                                    data-testid={`mark-read-${fb.feedback_id}`}
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => remove(fb.feedback_id)}
                                  className="p-1.5 rounded-md hover:bg-rose-100 text-rose-600"
                                  title="Excluir"
                                  data-testid={`delete-fb-${fb.feedback_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
          );
        })()}
      </div>
    </div>
  );
};

export default TeacherFeedback;
